import { isAxiosError } from 'axios'
import { default as axios, AUTH_REQUEST_TIMEOUT_MS } from './axiosConfig'
import { getEnv } from '@utils/env'
import {
  getAccessToken,
  getAccessTokenBearer,
} from '@services/auth/accessTokenStore'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

const API_DOMAIN = getEnv('REACT_APP_AUTH_API_DOMAIN', '')
const CLIENT_ID = getEnv('REACT_APP_CLIENT_ID', '')

/** Public client id only — no secret. Auth looks up the client; not proof of SPA origin. */
const clientAuthHeaders = (): Record<string, string> => {
  if (!CLIENT_ID) return {}
  return {
    'X-Client-Authorization': `Basic ${btoa(`${CLIENT_ID}:`)}`,
  }
}

// Types
export interface AuthRequestBody {
  username: string
  password: string
  scope: string
  /** When false, auth-service does not set the staff refresh cookie. */
  persist_session?: boolean
}

export interface GetAuthTokenOptions {
  persistSession?: boolean
}

export type LoginType = 'user' | 'staff'

export interface AuthResponseData {
  access_token: string
  /** Omitted for browser clients (HttpOnly cookie); present for non-browser scripts. */
  refresh_token?: string
  token_type: string
  user_id: string
  login_type: LoginType
  username: string
  user_role?: string
  user_scope: string[] | string
  tenant_id: string
  db_name: string
  provider_id?: string
}

export interface AuthResponse {
  statusCode: number
  message: string
  data: AuthResponseData
}

export interface LogoutResponseData {
  message: string
}

export interface LogoutResponse {
  statusCode: number
  message: string
  data: LogoutResponseData
}

export interface ResetRequestResponse {
  statusCode: number
  message: string
  data: { sent: boolean }
}

export interface ResetContextResponse {
  statusCode: number
  message: string
  data: { valid: boolean; username?: string; email?: string }
}

export interface ResetCompleteResponse {
  statusCode: number
  message: string
  data: { done: boolean }
}

export interface PwOtpRequestResponse {
  statusCode: number
  message: string
  data: { otp_id?: string; expires_at?: string }
}

export interface PwOtpVerifyResponse {
  statusCode: number
  message: string
  data: { step_token?: string; expires_at?: string }
}

export interface PwChangeCompleteResponse {
  statusCode: number
  message: string
  data: { done: boolean }
}

export interface TwilioAuthResponse {
  statusCode: number
  message: string
  data: {
    token: string
  }
}

export interface VerifyTokenResponseData {
  sub: string
  sub_typ?: LoginType
  username: string
  user_role?: string
  scp?: string
  user_scope?: string
  tenant_id: string
  db_name: string
  provider_id?: string
  [key: string]: any
}

export interface VerifyTokenResponse {
  statusCode: number
  message: string
  data: VerifyTokenResponseData
}

export type LocalUser = {
  user_id: string
  login_type?: LoginType
  username?: string
  user_role?: string
  user_scope?: string | string[]
  provider_id?: string
}

const authCredentialsConfig = {
  withCredentials: true as const,
  timeout: AUTH_REQUEST_TIMEOUT_MS,
  timeoutErrorMessage: 'Authentication timed out. Please try again.',
}

// API Functions
export const getAuthToken = async (
  username: string,
  password: string,
  scope: string,
  options?: GetAuthTokenOptions,
): Promise<AuthResponse> => {
  try {
    const body: AuthRequestBody = {
      username,
      password,
      scope,
      ...(options?.persistSession === false ? { persist_session: false } : {}),
    }

    const { data, status } = await axios.post<AuthResponse>(
      `${API_DOMAIN}/api/v1/auth/token?grant_type=password`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
        ...authCredentialsConfig,
      },
    )

    if (status !== 200) {
      throw new Error('Authentication failed')
    }

    return data
  } catch (e: unknown) {
    const status = isAxiosError(e) ? e.response?.status : undefined
    logger.error('Error in getAuthToken', { status })
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const logout = async (): Promise<LogoutResponse> => {
  try {
    const token = getAccessToken()
    const body = token ? { access_token: `Bearer ${token}` } : {}

    const { data, status } = await axios.post<LogoutResponse>(
      `${API_DOMAIN}/api/v1/auth/logout`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
        ...authCredentialsConfig,
      },
    )

    if (status !== 200) {
      throw new Error('Logout failed')
    }

    return data
  } catch (e: unknown) {
    const status = isAxiosError(e) ? e.response?.status : undefined
    logger.error('Error in logout', { status })
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getTwilioAuthToken = async (
  clinicId: string,
): Promise<TwilioAuthResponse> => {
  try {
    const token = getAccessToken()

    if (!token) {
      throw new Error('No access token found')
    }

    const { data, status } = await axios.post<TwilioAuthResponse>(
      `${API_DOMAIN}/api/v1/auth/twilio-token`,
      { clinic_id: clinicId, access_token: `Bearer ${token}` },
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
      },
    )

    if (status !== 200) {
      throw new Error('Authentication failed')
    }

    return data
  } catch (e: unknown) {
    const status = isAxiosError(e) ? e.response?.status : undefined
    logger.error('Error in getTwilioAuthToken', { status })
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const verifyToken = async (): Promise<VerifyTokenResponse> => {
  try {
    const token = getAccessToken()

    if (!token) {
      throw new Error('No access token found')
    }

    const { data, status } = await axios.post<VerifyTokenResponse>(
      `${API_DOMAIN}/api/v1/auth/verify`,
      { access_token: `Bearer ${token}` },
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
        ...authCredentialsConfig,
      },
    )

    if (status !== 200) {
      throw new Error('Token verification failed')
    }

    return data
  } catch (e: unknown) {
    const status = isAxiosError(e) ? e.response?.status : undefined
    logger.error('Error in verifyToken', { status })
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const fetchResetContext = async (
  token: string,
): Promise<ResetContextResponse> => {
  try {
    const { data, status } = await axios.get<ResetContextResponse>(
      `${API_DOMAIN}/api/v1/auth/password-reset/context`,
      {
        params: { token },
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
      },
    )
    if (status !== 200) {
      throw new Error('Context request failed')
    }
    return data
  } catch (e: unknown) {
    throw toApiError(e, 'Context request failed')
  }
}

export const completePwReset = async (params: {
  token: string
  password: string
}): Promise<ResetCompleteResponse> => {
  try {
    const { data, status } = await axios.post<ResetCompleteResponse>(
      `${API_DOMAIN}/api/v1/auth/password-reset/complete`,
      params,
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
      },
    )
    if (status !== 200) {
      throw new Error('Reset complete failed')
    }
    return data
  } catch (e: unknown) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const requestPwReset = async (params: {
  username: string
  email: string
}): Promise<ResetRequestResponse> => {
  try {
    const { data, status } = await axios.post<ResetRequestResponse>(
      `${API_DOMAIN}/api/v1/auth/password-reset/request`,
      params,
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
      },
    )
    if (status !== 200) {
      throw new Error('Reset request failed')
    }
    return data
  } catch (e: unknown) {
    throw toApiError(e, 'Reset request failed')
  }
}

const readAccessBearer = (): string => getAccessTokenBearer()

export const requestPwChangeOtp = async (): Promise<PwOtpRequestResponse> => {
  try {
    const { data, status } = await axios.post<PwOtpRequestResponse>(
      `${API_DOMAIN}/api/v1/auth/password-change/request-otp`,
      { access_token: readAccessBearer() },
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
      },
    )
    if (status !== 200) {
      throw new Error('Failed to send verification code')
    }
    return data
  } catch (e: unknown) {
    throw toApiError(e, 'Failed to send verification code')
  }
}

export const verifyPwChangeOtp = async (params: {
  otpId: string
  code: string
}): Promise<PwOtpVerifyResponse> => {
  try {
    const { data, status } = await axios.post<PwOtpVerifyResponse>(
      `${API_DOMAIN}/api/v1/auth/password-change/verify-otp`,
      {
        access_token: readAccessBearer(),
        otp_id: params.otpId,
        code: params.code,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
      },
    )
    if (status !== 200) {
      throw new Error('Invalid verification code')
    }
    return data
  } catch (e: unknown) {
    throw toApiError(e, 'Invalid verification code')
  }
}

export const completePwChange = async (params: {
  stepToken: string
  newPassword: string
}): Promise<PwChangeCompleteResponse> => {
  try {
    const { data, status } = await axios.post<PwChangeCompleteResponse>(
      `${API_DOMAIN}/api/v1/auth/password-change/complete`,
      {
        access_token: readAccessBearer(),
        step_token: params.stepToken,
        new_password: params.newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...clientAuthHeaders(),
        },
      },
    )
    if (status !== 200) {
      throw new Error('Failed to update password')
    }
    return data
  } catch (e: unknown) {
    throw toApiError(e, 'Failed to update password')
  }
}

export const trimProviderId = (raw?: string): string | undefined => {
  const t = raw?.toString().trim()
  return t && t.length > 0 ? t : undefined
}

export const parseUser = (): LocalUser | null => {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return null
    const u = JSON.parse(raw) as LocalUser
    if (!u?.user_id || typeof u.user_id !== 'string') return null
    return u
  } catch {
    return null
  }
}

export const getPid = (): string | null => {
  const id = trimProviderId(parseUser()?.provider_id)
  return id ?? null
}
